import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import CrispChat from "@/components/CrispChat";
import { lazy, Suspense } from "react";

// Critical routes loaded immediately
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";

// Lazy load non-critical routes
const Pricing = lazy(() => import("./pages/Pricing"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const HowToUse = lazy(() => import("./pages/HowToUse"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const TrialOptionsPage = lazy(() => import("./components/TrialOptionsPage"));
const PlanOptionsPage = lazy(() => import("./components/PlanOptionsPage"));

// Lazy load admin components
const AdminLayout = lazy(() => import("./components/AdminLayout"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Policies = lazy(() => import("./pages/Policies"));
const CreatePolicy = lazy(() => import("./pages/CreatePolicy"));
const PolicyHistory = lazy(() => import("./pages/PolicyHistory"));
const Rules = lazy(() => import("./pages/Rules"));
const RuleEditor = lazy(() => import("./pages/RuleEditor"));
const RuleTest = lazy(() => import("./pages/RuleTest"));
const ConsentLogs = lazy(() => import("./pages/ConsentLogs"));
const ConsentLogDetail = lazy(() => import("./pages/ConsentLogDetail"));
const ApiAccess = lazy(() => import("./pages/ApiAccess"));
const IntegrationGuide = lazy(() => import("./pages/IntegrationGuide"));
const Organization = lazy(() => import("./pages/Organization"));

// Loading component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <CrispChat 
            websiteId="b8813a05-2dcc-4f47-9cab-8ca57a871caf"
            autoload={false}
            onChatOpened={() => console.log('Crisp chat opened')}
            onChatClosed={() => console.log('Crisp chat closed')}
          />
          <BrowserRouter>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/how-to-use" element={<HowToUse />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/trial-options" element={<TrialOptionsPage />} />
                <Route path="/plan-options" element={<PlanOptionsPage />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/about" element={<About />} />
                <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/admin" element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }>
                <Route index element={<Dashboard />} />
                <Route path="policies" element={<Policies />} />
                <Route path="policies/new" element={<CreatePolicy />} />
                <Route path="policies/:policyId/history" element={<PolicyHistory />} />
                <Route path="rules" element={<Rules />} />
                <Route path="rules/new" element={<RuleEditor />} />
                <Route path="rules/test" element={<RuleTest />} />
                <Route path="rules/:ruleId/edit" element={<RuleEditor />} />
                <Route path="consent-logs" element={<ConsentLogs />} />
                <Route path="consent-logs/:consentId" element={<ConsentLogDetail />} />
                <Route path="api-access" element={<ApiAccess />} />
                <Route path="integration" element={<IntegrationGuide />} />
                <Route path="organization" element={<Organization />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </ThemeProvider>
  </QueryClientProvider>
);

export default App;
